<template>
  <el-card class="shiping-fee-details" :body-style="isMobile ? { paddingLeft: '8px', paddingRight: '8px', } : {}">
    <template v-if="tidyData.usedLogistics.length > 1" #header>
      <el-alert type="warning" show-icon>
        {{
          $t(
            "您目前選用了多個不同的物流，無法適用下列免運條件建議您使用相同的物流方式，以節省運費。"
          )
        }}
      </el-alert>
    </template>
    <div class="mb-3">
      <div class="flex items-center">
        <span class="text-gray-500 text-base">
          {{ $t("只要符合下列任一免運條件，本訂單即可免運") }}
        </span>
      </div>
    </div>
    <!-- 全站免運 -->
    <el-card v-if="data.free_delivery_amount_limit && itemsWithAllFreeShipping.length" class="mb-3 el-card-free-shipping"
      shadow="hover">
      <template #header>
        <el-alert type="warning" title="免運類型：全站" class="py-2" :closable="false">
          <span class="block text-gray-500 text-sm">
            再加入
            <span class="text-accent font-weight-bold">
              {{
                `${state.currency_symbol}${currencyFormat(
                  tidyData.amountBecomeFreeShipping
                )}`
              }}
            </span>
            {{ $t("可達免運條件") }}
            <a v-if="store.getters['checkout/refUrl']" :href="`/goto?url=${encodeURIComponent(
              store.getters['checkout/refUrl']
            )}`" :style="{ float: 'right' }" class="text-accent text--accent-3 text-sm">
              {{ $t("繼續逛逛") }}
            </a>
          </span>
        </el-alert>
      </template>
      <div class="flex flex-col">
        <div v-for="(n, index) in itemsWithAllFreeShipping" :key="n.cart_id" class="py-2">
          <div class="flex items-center">
            <div class="flex-grow">
              <span class="block text-sm">{{ n.product_name }}</span>
              <span>{{ n.assort_name }} </span>
              <span> x {{ n.quantity }} </span>
            </div>
            <div class="w-32 text-right">
              <span class="text-accent darken-1">
                {{ `${n.symbol}${currencyFormat(n.sub_total)}` }}
              </span>
            </div>
          </div>
          <el-divider v-if="index < itemsWithAllFreeShipping.length - 1" style="margin: 14px 0px 0px;" />
        </div>
      </div>
    </el-card>
    <!-- 活動免運 -->
    <el-card v-for="g in cartEventGroupsFormattedWithPromotion" :key="g.cart_group_key" class="mb-3" shadow="hover">
      <template #header>
        <el-alert type="success" title="免運類型：活動" show-icon class="py-2 mb-2">
          <span class="block text-accent text-sm sm:text-xs">
            {{ g.cart_group_name }}
          </span>
          <span class="text-gray-500 text-sm" :class="g?.promotion?.is_excluded_discount ||
            g?.promotion?.is_excluded_outer_island_free_shipping
            ? ['block']
            : []
            ">
            {{ $t("再加入") }}
            <span class="text-accent font-weight-bold">
              {{
                `${state.currency_symbol}${currencyFormat(g.amountBecomeFreeShipping)}`
              }}
            </span>
            {{ $t("可達免運條件") }}
          </span>
          <el-tag v-if="g?.promotion?.is_excluded_discount" type="danger" size="small">
            {{ $t("不適用其他折扣") }}
          </el-tag>
          <el-tag v-if="g?.promotion?.is_excluded_outer_island_free_shipping" type="danger" size="small">
            {{ $t("外島不適用") }}
          </el-tag>
          <a v-if="store.getters['checkout/refUrl']" :href="`/goto?url=${encodeURIComponent(
            store.getters['checkout/refUrl']
          )}`" :style="{ float: 'right' }" class="text-accent text--accent-3">
            {{ $t("繼續逛逛") }}
          </a>
        </el-alert>
      </template>
      <div class="flex flex-col">
        <div v-for="(n, index) in g.items" :key="n.cart_id" class="py-2">
          <div class="flex items-center">
            <div class="flex-grow truncate">
              <span class="text-sm">{{
                n.product_name
              }}</span>
              <span>{{ n.assort_name }} </span>
              <span> x {{ n.quantity }} </span>
            </div>
            <div class="w-32 text-right">
              <span class="text-accent darken-1">
                {{ `${n.symbol}${currencyFormat(n.sub_total)}` }}
              </span>
            </div>
          </div>
          <el-divider v-if="index < g.items.length - 1" style="margin: 14px 0px 0px;" />
        </div>
      </div>
    </el-card>
  </el-card>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from 'vuex'
import { useI18n } from "vue-i18n"
import { currencyFormat, breakpoint } from "../../../utils";
import { keyBy } from "lodash";

const props = defineProps({
  form: Object,
  state: Object,
  data: Object,
  cartGroupsFormatted: Object,
  cartEventGroupsFormatted: Object,
});

const emit = defineEmits(["close"]);

const { t, n, locale } = useI18n()
const store = useStore()

const isMobile = computed(() => breakpoint.value.xs || breakpoint.value.sm)

const tidyData = computed(() => {
  let amount_become_free_shipping = 0;
  Object.entries(props.cartGroupsFormatted).map(([k, v]) => {
    if (k !== "unchecked") {
      amount_become_free_shipping = v.amount_become_free_shipping;
    }
  });
  return {
    usedLogistics: [...new Set(props.form.carts.map((v) => v.delivery_type))],
    amountBecomeFreeShipping: amount_become_free_shipping,
  };
});

const cartEventGroupsFormattedWithPromotion = computed(() => {
  const promotionMap = keyBy(props.data.promotions, "id");
  //   console.warn("promotionMap", promotionMap);
  return Object.entries(props.cartEventGroupsFormatted)
    .map(([key, val]) => val)
    .filter((val) => val.prom_id)
    .map((val) => {
      const promotion = promotionMap?.[`${val.prom_id}`];
      const items = val.items.filter((item) => item.checked && item.is_valid);
      let amountBecomeFreeShipping = 0;
      if (promotion?.min_amount_free_shipping) {
        amountBecomeFreeShipping = promotion.min_amount_free_shipping;
        items.map((item) => {
          amountBecomeFreeShipping -= item.sub_total;
        });
      }
      return { ...val, items, promotion, amountBecomeFreeShipping };
    })
    .filter(
      (val) => val.promotion?.min_amount_free_shipping && val.items.length
    );
});

const itemsWithAllFreeShipping = computed(() => {
  const items = [];
  Object.entries(props.cartEventGroupsFormatted).map(([key, val]) => {
    items.push(
      ...val.items.filter(
        (item) => item.checked && item.is_valid && !item.is_excluded_discount
      )
    );
  });
  return items;
});

const emitClose = () => {
  emit("close");
};

</script>
<style lang="scss">
.shiping-fee-details {
  content: "";

  .el-card-free-shipping {
    .el-card__header {
      padding: 0;

      .el-alert__content {
        padding-top: 8px;

        .el-alert__title {
          font-size: large;
        }
      }
    }

    .el-card__body {
      padding-top: 0;
      padding-bottom: 0;
    }

  }
}
</style>